/**
 * Shrink a phone photo before it is uploaded.
 *
 * Originals of 3–10 MB made each upload slow on site (mobile data) and were
 * re-embedded at full size in the PDF. 2560 px on the long side keeps every
 * detail an expert points at, at a fraction of the weight. Anything that
 * cannot be decoded here (HEIC on some browsers, non-images) is sent as is.
 */
export const MAX_SIDE = 2560;
const QUALITY = 0.85;
// Small enough already: re-encoding would only cost quality.
const SKIP_BELOW_BYTES = 800 * 1024;

export async function compressImage(file, { maxSide = MAX_SIDE, quality = QUALITY } = {}) {
  if (!file || !file.type || !file.type.startsWith('image/') || file.type === 'image/gif') return file;
  if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') return file;

  let bitmap;
  try {
    // 'from-image' applies the EXIF orientation, so portrait shots stay upright.
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    return file;
  }
  try {
    const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
    if (scale === 1 && file.size < SKIP_BELOW_BYTES) return file;

    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
    if (!blob || blob.size >= file.size) return file;

    const name = (file.name || 'photo').replace(/\.[^.]+$/, '') + '.jpg';
    return new File([blob], name, { type: 'image/jpeg', lastModified: file.lastModified });
  } finally {
    if (bitmap.close) bitmap.close();
  }
}
